import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
// custom components
import ItemGroup from './ItemGroup';
import SoloMenuItem from './SoloMenuItem';
import SubMenu from './SubMenu';

import { StyledMenu, StyledMenuItem } from './styles';
// context
import { useMenu } from '../../../contexts/Menu';
// RBAC
import WithAdmin from '../../../helpers/roles/WithAdmin';
import WithShopManager from '../../../helpers/roles/WithShopManager';
import { authContext } from '../../../contexts/Auth';
import useGetMe from '@/store/Sessions/useGetMe';

import { useFlags } from '@/contexts/Flags';
import env from '@/env';

const Menu: React.FC = () => {
    const history = useHistory();
    const { isCollapsed, setCollapsed } = useMenu();

    const flags = useFlags();

    const { data, isAssuming } = useContext(authContext);
    const { data: extUser } = useGetMe();

    const getMenuProps = (pathname: string) => {
        return {
            key: pathname,
            onClick: () => {
                setCollapsed(true);
                history.push(pathname);
            },
        };
    };

    const handlers = useSwipeable({
        delta: 75,
        onSwipedLeft: () => setCollapsed(true),
        trackTouch: true,
        trackMouse: true,
        preventDefaultTouchmoveEvent: true,
    });

    const isStaging = ['staging', 'development'].includes(env.ENV);
    const churnUsers = isStaging
        ? flags?.USERS_TO_ACCESS_SCREEN_CHURN?.users_stg ?? []
        : flags?.USERS_TO_ACCESS_SCREEN_CHURN?.users_prod ?? [];

    // protected menus
    const ProtectedAdminMenu = WithAdmin(() => (
        <StyledMenu
            mode={!isCollapsed ? 'vertical' : 'inline'}
            selectedKeys={[history.location.pathname]}
        >
            <SubMenu
                isCollapsed={!isCollapsed}
                key="sub1"
                title="Usuários"
                icon={null}
            >
                <ItemGroup key="g1" title="Administrador">
                    <StyledMenuItem {...getMenuProps('/users/admin/list')}>
                        Gerenciar Administradores
                    </StyledMenuItem>
                </ItemGroup>

                <ItemGroup key="g2" title="Lojista">
                    <StyledMenuItem
                        {...getMenuProps('/users/shopmanager/list')}
                    >
                        Gerenciar Lojistas
                    </StyledMenuItem>
                </ItemGroup>
                <ItemGroup key="g3" title="Cliente">
                    <StyledMenuItem {...getMenuProps('/users/client/list')}>
                        Gerenciar Clientes
                    </StyledMenuItem>
                </ItemGroup>
            </SubMenu>
            <SoloMenuItem
                isCollapsed={!isCollapsed}
                title="Lojas"
                {...getMenuProps('/stores')}
            />
            <SoloMenuItem
                isCollapsed={!isCollapsed}
                title="Estoque"
                {...getMenuProps('/stocks/list')}
            />
            <SoloMenuItem
                isCollapsed={!isCollapsed}
                title="Produtos"
                {...getMenuProps('/product_global/list')}
            />
            <SoloMenuItem
                isCollapsed={!isCollapsed}
                title="Vendas"
                {...getMenuProps('/sales/list')}
            />

            {flags && flags.PUSH_NOTIFICATION_V2 ? (
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Notificações PUSH"
                    {...getMenuProps('/notifications/v2/list')}
                />
            ) : (
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Notificações PUSH"
                    {...getMenuProps('/notifications/list')}
                />
            )}

            <SoloMenuItem
                isCollapsed={!isCollapsed}
                title="Importações"
                {...getMenuProps('/imports/reports')}
            />
            <SoloMenuItem
                isCollapsed={!isCollapsed}
                title="Logs de notificação"
                {...getMenuProps('/notification/logs')}
            />
            <SoloMenuItem
                isCollapsed={!isCollapsed}
                title="Anúncios"
                {...getMenuProps('/adverts/admin/list')}
            />
            <SoloMenuItem
                isCollapsed={!isCollapsed}
                title="Comunicados"
                {...getMenuProps('/announcements')}
            />
            <SoloMenuItem
                isCollapsed={!isCollapsed}
                title="Gerenciamento de totens"
                {...getMenuProps('/totems')}
            />
            <SoloMenuItem
                isCollapsed={!isCollapsed}
                title="Boletos e Cobranças"
                {...getMenuProps('/invoice')}
            />
            {flags && flags.CHARGE_LOOSE && (
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Cobrança Automática Avulsa"
                    {...getMenuProps('/charges')}
                />
            )}
            {churnUsers.includes(data?.user?.email || '') && (
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Risco de Churn "
                    {...getMenuProps('/churn')}
                />
            )}
        </StyledMenu>
    ));

    const ProtectedShopManagerMenu = WithShopManager(() => (
        <StyledMenu
            mode={!isCollapsed ? 'vertical' : 'inline'}
            selectedKeys={[history.location.pathname]}
        >
            <SubMenu
                isCollapsed={!isCollapsed}
                key="sub9"
                title="Gerenciar"
                icon={null}
            >
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Lojas"
                    {...getMenuProps('/stores/list')}
                />
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Produtos"
                    {...getMenuProps('/products/list')}
                />

                {flags && flags.PUSH_NOTIFICATION_V2 ? (
                    <SoloMenuItem
                        isCollapsed={!isCollapsed}
                        title="Notificações PUSH"
                        {...getMenuProps('/notifications/v2/list')}
                    />
                ) : (
                    <SoloMenuItem
                        isCollapsed={!isCollapsed}
                        title="Notificações PUSH"
                        {...getMenuProps('/notifications/list')}
                    />
                )}

                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Anúncios"
                    {...getMenuProps('/adverts/list')}
                />
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Transferências"
                    {...getMenuProps('/transfer/list')}
                />
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Saldo em Carteira"
                    {...getMenuProps('/wallet/balance')}
                />
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Cupons"
                    {...getMenuProps('/coupons/list')}
                />
                {flags && flags.AUTOMATION_PLANOGRAM && (
                    <SoloMenuItem
                        isCollapsed={!isCollapsed}
                        title="Planogramas"
                        {...getMenuProps('/planogram/automation')}
                    />
                )}
                {flags &&
                    !flags.AUTOMATION_PLANOGRAM &&
                    flags?.AUTOMATION_PLANOGRAM_USERS?.users?.includes(
                        `${data?.user?.id as number}`,
                    ) && (
                        <SoloMenuItem
                            isCollapsed={!isCollapsed}
                            title="Planogramas"
                            {...getMenuProps('/planogram/automation')}
                        />
                    )}

                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Usuários"
                    {...getMenuProps('/shopmanagers/users/list')}
                />
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Boletos e Cobranças"
                    {...getMenuProps('/invoice')}
                />
                {!!data.user?.shopManager?.emit_invoice && (
                    <SoloMenuItem
                        isCollapsed={!isCollapsed}
                        title="Notas Fiscais"
                        {...getMenuProps('/nfe')}
                    />
                )}
            </SubMenu>
            <SubMenu
                isCollapsed={!isCollapsed}
                key="sub6"
                title="Relatórios"
                icon={null}
            >
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Estoques"
                    {...getMenuProps('/stocks/list')}
                />
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Ajustes de Estoque"
                    {...getMenuProps('/stocks/adjustments')}
                />
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Compras"
                    {...getMenuProps('/purchase/list')}
                />
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Vendas"
                    {...getMenuProps('/sales/list')}
                />
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Financeiro"
                    {...getMenuProps('/financial')}
                />
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Importações"
                    {...getMenuProps('/imports/reports')}
                />
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Produtos"
                    {...getMenuProps('/products/reports')}
                />
            </SubMenu>
            <SoloMenuItem
                isCollapsed={!isCollapsed}
                title="Comunicados"
                {...getMenuProps('/announcements/view')}
            />
            {!!extUser?.shopManager?.can_change_tax_data && !isAssuming && (
                <SoloMenuItem
                    isCollapsed={!isCollapsed}
                    title="Configurações Fiscais"
                    {...getMenuProps('/shopmanager/tax-data')}
                />
            )}
        </StyledMenu>
    ));

    return (
        <div {...handlers} style={{ height: '100%' }}>
            <StyledMenu
                mode={!isCollapsed ? 'vertical' : 'inline'}
                selectedKeys={[history.location.pathname]}
            >
                <ItemGroup>
                    <SoloMenuItem
                        isCollapsed={!isCollapsed}
                        title="Dashboard"
                        icon={null}
                        {...getMenuProps('/')}
                    />

                    <ProtectedShopManagerMenu />
                    <ProtectedAdminMenu />
                </ItemGroup>
            </StyledMenu>
        </div>
    );
};

export default Menu;
